// @flow
import * as React from 'react';
import classNames from 'classnames';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import DateFooter from 'components/atoms/DateFooter';
import InvisibleRecaptcha from 'components/molecules/InvisibleRecaptcha';

// Enums
import { CONFIRMATION_ROUTE } from 'constants/routes';

// Constants
import { NAVIGATION as FORWARD } from 'constants/navigations';
import { TRANSPORTATION } from 'constants/transportation';
import { GTM_API, GTM_RECAPTCHA_EVENTS } from 'constants/recaptcha';

// Types
import { gtmUserEventType } from 'types/gtm';
import { type TotalPrice } from 'types/price';
import type { CTA } from 'api/xws/optimizely/optimizely';

// Utils
import { setTransportationLabel } from 'utils/transportationLabel';
import { isTrue } from 'utils/varValidation';
import { formatTime } from 'utils/date';

import './BookItButton.scss';

const ns = 'book-it-button';
const { VALET, VALETLOANER } = TRANSPORTATION;
const { confirm, reschedule } = GTM_API;
type Props = {
  notes: string,
  goTo: (path: string, direction: string) => void,
  gtmUserEvent: gtmUserEventType,
  bookAppt: () => void,
  isBookingEnabled: Object,
  appointmentStatus: string,
  modal: boolean,
  transportationOption: string,
  transportations: Array<Object>,
  privacySettings: Object,
  openModal: () => void,
  throwErrorBooking: () => void,
  isApptModified: boolean,
  isModifyEnabled: boolean,
  isDmsAppt: boolean,
  totalPrice?: TotalPrice,
  isLeadAppt: boolean,
  isNewLeadAppt: boolean,
  startTime: string,
  isContactInfoFormValid: boolean,
  isFormRemindersValid: boolean,
  isLicenseNumberValid: boolean,
  setContactInfoMissing: boolean => void,
  intl: IntlShape,
  mobilePhoneNumberPopulated: () => void,
  getOptStatus: () => Object | boolean,
  marketingSMSConfiguration: boolean,
  getOptInUrl: () => string | boolean,
  externalValet: string,
  isFromCTA: boolean,
  ctaBanner: CTA | null,
  isCarryOver: boolean,
  showBookItButtonLabel: boolean,
  isModifyAppt: boolean,
  setRecaptchaToken: string => void,
  isRecaptchaEnabledOnConfirmAppt: boolean,
  isRecaptchaEnabledOnRescheduleAppt: boolean,
  languageCode: string,
  order?: number,
  gtmRecaptchaEvent: (event: string, api: string, token: string) => void
};

type State = {
  activeRunning: boolean
};

class BookItButton extends React.Component<Props, State> {
  static defaultProps = {
    modal: false
  };
  constructor(props: Props, context: any) {
    super(props, context);
    if (
      props.isRecaptchaEnabledOnConfirmAppt ||
      props.isRecaptchaEnabledOnRescheduleAppt
    ) {
      this.recaptcha = React.createRef();
    }
  }
  state = {
    activeRunning: false,
    isListeningForRecaptcha: false
  };

  onBookIt = async () => {
    const {
      isBookingEnabled,
      goTo,
      bookAppt,
      gtmUserEvent,
      notes,
      privacySettings,
      transportationOption,
      transportations,
      isApptModified,
      totalPrice,
      throwErrorBooking,
      intl,
      getOptStatus,
      marketingSMSConfiguration,
      getOptInUrl,
      isFromCTA,
      ctaBanner,
      gtmRecaptchaEvent,
      isModifyAppt,
      recaptchaToken,
      sendScheduleServiceReviewFinish,
      sendCustomerInfoFormFinish
    } = this.props;
    const ctaGtm = isFromCTA && ctaBanner ? ` CTA` : '';
    const selectedTransportationLabel = transportations.find(
      ({ data }) => data === transportationOption
    );
    const {
      emailPrivacySetting: email,
      textPrivacySetting: text
    } = privacySettings;
    if (isBookingEnabled) {
      this.setState({
        activeRunning: true
      });
      try {
        let toggleGTMValue = null;
        if (email && text) {
          toggleGTMValue = 'Email + Text';
        } else if (email) {
          toggleGTMValue = 'Email Only';
        } else if (text) {
          toggleGTMValue = 'Text Only';
        }
        if (toggleGTMValue) {
          gtmUserEvent(
            'Usage Of The Email And Text Toggles',
            'Usage',
            `${toggleGTMValue}${ctaGtm}`,
            'GTM_CURRENT_PAGE > Book It'
          );
        }
        if (notes) {
          gtmUserEvent(
            'Usage Of The Additional Questions Field',
            'Usage',
            `Usage${ctaGtm}`,
            'GTM_CURRENT_PAGE > Book It',
            notes
          );
        }
        if (selectedTransportationLabel) {
          gtmUserEvent(
            'Usage Of Transportation Option Label',
            'Usage',
            'Usage',
            'GTM_CURRENT_PAGE > Book It',
            setTransportationLabel(
              selectedTransportationLabel && selectedTransportationLabel.label
                ? selectedTransportationLabel.label
                : {},
              intl
            ) +
              ' - ' +
              transportationOption
          );
        }
        const successBookIt = await bookAppt();
        if (successBookIt) {
          if (isTrue(marketingSMSConfiguration)) {
            getOptStatus();
            getOptInUrl();
          }
          sendScheduleServiceReviewFinish();
          sendCustomerInfoFormFinish();
          goTo(CONFIRMATION_ROUTE, FORWARD);
        } else {
          throwErrorBooking();
          return null;
        }
        if (!isApptModified) {
          gtmUserEvent(
            'New Appointment',
            'Booked',
            `New Appointment Booked${ctaGtm}`,
            'GTM_CURRENT_PAGE > Book It',
            totalPrice && totalPrice.price
              ? `Appointment Price: ${totalPrice.price}`
              : ''
          );
        } else {
          gtmUserEvent(
            'Modify Appointment',
            'Modified',
            `Appointment Modified${ctaGtm}`,
            'GTM_CURRENT_PAGE > Book It'
          );
        }
        gtmRecaptchaEvent(
          GTM_RECAPTCHA_EVENTS.API_CALL,
          isModifyAppt ? reschedule : confirm,
          recaptchaToken,
          successBookIt
        );
        goTo(CONFIRMATION_ROUTE, FORWARD);
      } catch (err) {
        /* eslint-disable no-console */
        console.error(err);
        /* eslint-enable no-console */
        this.setState({
          activeRunning: false
        });
      }
    }
  };

  bookAppointment = async () => {
    const {
      isModifyAppt,
      isRecaptchaEnabledOnConfirmAppt,
      isRecaptchaEnabledOnRescheduleAppt
    } = this.props;
    const { isListeningForRecaptcha } = this.state;
    if (
      (!isModifyAppt && isRecaptchaEnabledOnConfirmAppt) ||
      (isModifyAppt && isRecaptchaEnabledOnRescheduleAppt)
    ) {
      !isListeningForRecaptcha &&
        this.recaptcha.current.initRecaptchaListener();
      this.setState({ isListeningForRecaptcha: true });
      this.recaptcha.current.execute();
    } else {
      await this.onBookIt();
    }
  };

  onClick = () => {
    const {
      isApptModified,
      isModifyEnabled,
      isDmsAppt,
      openModal,
      isLeadAppt,
      isNewLeadAppt,
      isContactInfoFormValid,
      isFormRemindersValid,
      isLicenseNumberValid,
      setContactInfoMissing,
      mobilePhoneNumberPopulated,
      isCarryOver
    } = this.props;
    const isCancel = (isModifyEnabled && !isApptModified) || isDmsAppt;
    if (isCarryOver) {
      return;
    }
    if (isLeadAppt && isNewLeadAppt) {
      this.bookAppointment();
    } else if (isCancel) {
      openModal();
    } else if (
      isContactInfoFormValid &&
      isFormRemindersValid &&
      isLicenseNumberValid
    ) {
      mobilePhoneNumberPopulated();
      setContactInfoMissing(false);
      if (!isModifyEnabled || (isLeadAppt && isNewLeadAppt) || isApptModified) {
        this.bookAppointment();
      }
    } else {
      setContactInfoMissing(true);
    }
  };

  getLabelConfig(text: string, showDate?: boolean) {
    const { showBookItButtonLabel } = this.props;
    return showDate && !showBookItButtonLabel ? (
      <DateFooter
        date={this.props.startTime}
        scheduledInfo={true}
        text={text}
      />
    ) : (
      <span>{text}</span>
    );
  }

  labelButton() {
    const {
      isApptModified,
      isModifyEnabled,
      isDmsAppt,
      isLeadAppt,
      isNewLeadAppt,
      intl,
      externalValet,
      isFromCTA,
      isCarryOver,
      transportationOption,
      showBookItButtonLabel
    } = this.props;
    const isValet =
      transportationOption === VALET || transportationOption === VALETLOANER;
    if (isLeadAppt && isNewLeadAppt) {
      return this.getLabelConfig(
        intl
          .formatMessage({
            id: 'c8.review.button.confirm'
          })
          .toLowerCase()
      );
    }
    if ((isModifyEnabled && !isApptModified && !isCarryOver) || isDmsAppt) {
      let cancelApptLabel = intl.formatMessage({
        id: 'c8.review.button.cancel.appointment',
        defaultMessage: 'CANCEL APPOINTMENT'
      });
      cancelApptLabel = isFromCTA
        ? cancelApptLabel.toUpperCase()
        : cancelApptLabel.toLowerCase();
      return this.getLabelConfig(cancelApptLabel, !isFromCTA);
    }
    if (isFromCTA) {
      return this.getLabelConfig(
        intl.formatMessage({
          id: 'c8.review.button.book.service',
          defaultMessage: 'BOOK SERVICE'
        })
      );
    }
    if (!this.state.activeRunning && !isCarryOver) {
      const withLabelText = intl.formatMessage({
        id: 'c8.review.book.service.appointment',
        defaultMessage: 'Book Service Appointment'
      });
      const noLabelText = intl.formatMessage({
        id:
          externalValet && isValet
            ? 'c8.review.button.pickup'
            : 'c8.review.schedule'
      });
      const text = showBookItButtonLabel
        ? withLabelText
        : noLabelText.substr(0, noLabelText.lastIndexOf(' ')).toLowerCase();
      return this.getLabelConfig(text, true);
    }
    return this.getLabelConfig('');
  }

  render() {
    const {
      isBookingEnabled,
      modal,
      isApptModified,
      isDmsAppt,
      isModifyEnabled,
      isLeadAppt,
      isNewLeadAppt,
      intl,
      isCarryOver,
      startTime,
      isFromCTA,
      showBookItButtonLabel,
      setRecaptchaToken,
      isRecaptchaEnabledOnConfirmAppt,
      isRecaptchaEnabledOnRescheduleAppt,
      languageCode,
      gtmRecaptchaEvent,
      order,
      isModifyAppt
    } = this.props;
    const { activeRunning } = this.state;
    const renderRecaptcha =
      isRecaptchaEnabledOnConfirmAppt || isRecaptchaEnabledOnRescheduleAppt;

    const bookButtonClassNames = classNames(`${ns} button__btn-book`, {
      running: activeRunning,
      'ld-ext-right':
        isBookingEnabled ||
        (isBookingEnabled &&
          ((isLeadAppt && isNewLeadAppt) ||
            (isModifyEnabled && isApptModified))),
      'cancel-button':
        isCarryOver ||
        isDmsAppt ||
        (isBookingEnabled &&
          isModifyEnabled &&
          !isApptModified &&
          !(isLeadAppt && isNewLeadAppt)),
      'read-only-button': isCarryOver
    });
    const footerClassName = classNames({ 'd-none': !isBookingEnabled });
    const buttonGridSystem = { xs: 12 };
    let ariaLabelDateTime = '';

    if (startTime) {
      const labelDate = intl.formatDate(startTime, {
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      });
      const labelTime = formatTime((startTime || '').toString(), intl);
      ariaLabelDateTime = `${labelDate} ${labelTime}`;
    }
    let ariaId = 'c8.aria.schedule.appointment.for';
    let ariaDefaultMessage = 'Schedule appointment for {ariaLabelDateTime}';

    // is cancel appointment
    if ((isModifyEnabled && !isApptModified) || isDmsAppt) {
      ariaId = 'c8.aria.cancel.appointment.for';
      ariaDefaultMessage = 'Cancel appointment for {ariaLabelDateTime}';
    }
    if (isCarryOver) {
      ariaId = 'c8.aria.appointment.selected';
      ariaDefaultMessage = 'Appointment scheduled for {ariaLabelDateTime}';
    }

    return (
      <AppointmentFlowFooter
        className={modal ? footerClassName : ''}
        buttonGridSystem={buttonGridSystem}
        onClick={() => this.onClick()}
        ariaLabel={''}
        readOnly={isCarryOver || isDmsAppt}
        buttonId={isFromCTA ? 'cta-continue-button' : ''}
      >
        {!isFromCTA &&
          showBookItButtonLabel && (
            <div className={`${ns}__book-it-label`}>
              {intl.formatDate(startTime, {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}{' '}
              {intl.formatMessage({
                id: 'c8.review.button.label.at',
                defaultMessage: 'at'
              })}{' '}
              {formatTime(startTime, intl, false, false)}
            </div>
          )}
        <div
          className={bookButtonClassNames}
          tabIndex={0}
          aria-label={intl.formatMessage(
            {
              id: ariaId,
              defaultMessage: ariaDefaultMessage
            },
            {
              ariaLabelDateTime
            }
          )}
        >
          {!activeRunning && (
            <div className="button-label">{this.labelButton()}</div>
          )}
          {isBookingEnabled && <div className="ld ld-ring ld-spin-fast" />}
        </div>
        {renderRecaptcha && (
          <InvisibleRecaptcha
            setRecaptchaToken={setRecaptchaToken}
            languageCode={languageCode}
            gtmAction={gtmRecaptchaEvent}
            gtmApi={isModifyAppt ? reschedule : confirm}
            order={order}
            onResolveAction={async () => {
              this.setState({
                isListeningForRecaptcha: false
              });
              await this.onBookIt();
            }}
            ref={this.recaptcha}
          />
        )}
      </AppointmentFlowFooter>
    );
  }
}

export default BookItButton;



// WEBPACK FOOTER //
// ./src/components/molecules/BookItButton/BookItButton.js